import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import {
  Target,
  AlertTriangle,
  BarChart3,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Download,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  X,
  Save,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import opportunityPipelineService from '../../../api/crm/opportunityPipeline/opportunityPipelineService';
import leadService from '../../../api/crm/leads/leadService';
import { leadStatusService } from '../../../api/crm/config/configService';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatIndianAmount, formatIndianNumber } from '../../../utils/amountFormatter';
import { truncateText } from '../../../utils/stringUtils';
import {
  handleSearchInputChange,
  sanitizeTextInput,
  validateNumberInput,
  getCharacterCount,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import toast from 'react-hot-toast';
import OpportunityEditModal from './components/OpportunityEditModal';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

function sortPipelineOpportunities(items, sortColumn, sortDirection) {
  if (!sortColumn) {
    return items;
  }

  const sorted = [...items].sort((a, b) => {
    let aValue = a[sortColumn];
    let bValue = b[sortColumn];

    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    if (sortColumn === 'value') {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    }

    if (sortColumn === 'expectedClose' || sortColumn === 'expectedCloseDate') {
      if (a.expectedCloseDate) aValue = new Date(a.expectedCloseDate).getTime();
      if (b.expectedCloseDate) bValue = new Date(b.expectedCloseDate).getTime();
    }

    if (sortColumn === 'convertedAt') {
      aValue = a.convertedAtRaw ? new Date(a.convertedAtRaw).getTime() : 0;
      bValue = b.convertedAtRaw ? new Date(b.convertedAtRaw).getTime() : 0;
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  return sorted;
}

const PipelineDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [notification, setNotification] = useState(null);

  // Initial filter values from URL (for persistence across refresh)
  const initialSearch = searchParams.get('search') || '';
  const initialStatus = searchParams.get('status') || 'all';
  const initialStartDate = searchParams.get('startDate') || '';
  const initialEndDate = searchParams.get('endDate') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialLimit = Number(searchParams.get('limit')) || 10;
  const initialSortBy = searchParams.get('sortBy') || null;
  const initialSortDir = searchParams.get('sortOrder') || 'asc';

  // Opportunity List States
  const [convertedOpportunities, setConvertedOpportunities] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [loadingOpportunities, setLoadingOpportunities] = useState(true);
  const [opportunitySearchTerm, setOpportunitySearchTerm] = useState(initialSearch);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [dateRange, setDateRange] = useState({ startDate: initialStartDate, endDate: initialEndDate });
  const [opportunitiesItemsPerPage, setOpportunitiesItemsPerPage] = useState(initialLimit);
  const [opportunitiesCurrentPage, setOpportunitiesCurrentPage] = useState(initialPage);
  const [opportunityPagination, setOpportunityPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });
  // Sorting States
  const [sortColumn, setSortColumn] = useState(initialSortBy);
  const [sortDirection, setSortDirection] = useState(initialSortDir); // 'asc' or 'desc'

  // Sync filter/pagination/sort state to URL for persistence across refresh
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = ['search', 'status', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (opportunitySearchTerm.trim()) params.set('search', opportunitySearchTerm.trim());
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (opportunitiesCurrentPage && opportunitiesCurrentPage !== 1) params.set('page', String(opportunitiesCurrentPage));
      if (opportunitiesItemsPerPage && opportunitiesItemsPerPage !== 10) params.set('limit', String(opportunitiesItemsPerPage));
      if (sortColumn) {
        params.set('sortBy', sortColumn);
        if (sortDirection && sortDirection !== 'asc') params.set('sortOrder', sortDirection);
      }

      return params;
    }, { replace: true });
  }, [
    opportunitySearchTerm,
    statusFilter,
    dateRange,
    opportunitiesCurrentPage,
    opportunitiesItemsPerPage,
    sortColumn,
    sortDirection,
    setSearchParams,
  ]);

  // Modal States
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [opportunityToDelete, setOpportunityToDelete] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Selection and export state
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] =
    useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] =
    useState(false);
  const selectedExportWrapRef = useRef(null);
  const { data: dashboardData, isLoading, isError, error } = useQuery({
    queryKey: ['pipelineDashboard'],
    queryFn: async () => {
      const response = await opportunityPipelineService.getPipelineStats();
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to load dashboard data');
      }
    },
    refetchOnWindowFocus: false,
    retry: false
  });

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle sort direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedOpportunities = useMemo(
    () => sortPipelineOpportunities(convertedOpportunities, sortColumn, sortDirection),
    [convertedOpportunities, sortColumn, sortDirection]
  );

  const getAttainmentColor = (attainment) => {
    if (attainment >= 100) return 'text-green-600';
    if (attainment >= 90) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'On Track': return 'bg-green-100 text-green-800';
      case 'At Risk': return 'bg-yellow-100 text-yellow-800';
      case 'Behind': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleFilterChange = () => {
    // Logic if needed
  };

  // Fetch converted opportunities
  const fetchConvertedOpportunities = useCallback(async () => {
    setLoadingOpportunities(true);
    try {
      const filters = {
        search: opportunitySearchTerm,
        page: opportunitiesCurrentPage,
        limit: opportunitiesItemsPerPage,
        stage: statusFilter
      };

      if (dateRange.startDate) filters.startDate = dateRange.startDate;
      if (dateRange.endDate) filters.endDate = dateRange.endDate;

      const result = await leadService.getConvertedLeads(filters);

      if (result.success) {
        // Map converted leads to the format needed for display
        const mappedOpportunities = result.leads.map(lead => {
          const leadName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.name || 'N/A';
          const companyName = lead.company || 'N/A';

          const oppName = lead.opportunityName || `${companyName} - Opportunity`;
          const oppValue = lead.opportunityValue || 0;
          const oppExpectedCloseDate = lead.opportunityExpectedCloseDate;
          const oppDescription = lead.opportunityDescription || '';

          let expectedCloseFormatted = 'N/A';
          if (oppExpectedCloseDate) {
            const closeDate = new Date(oppExpectedCloseDate);
            expectedCloseFormatted = closeDate.toLocaleDateString('en-IN', {
              day: '2-digit', month: 'short', year: 'numeric'
            });
          }

          let convertedAtFormatted = 'N/A';
          if (lead.conversionDate) {
            const convertedDate = new Date(lead.conversionDate);
            convertedAtFormatted = `${convertedDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}, ${convertedDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
          }

          const stageDisplay = (() => {
            if (lead.stage === 'closed_won') return 'Won';
            if (lead.stage === 'closed_lost') return 'Lost';
            if (lead.stage === 'converted') return 'Open';
            return lead.stage?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Open';
          })();

          return {
            id: lead._id || lead.id,
            _id: lead._id,
            opportunityId: lead.leadId,
            opportunityName: oppName,
            leadName: leadName,
            company: companyName,
            stage: stageDisplay,
            stageValue: lead.stage || 'converted',
            value: oppValue,
            expectedClose: expectedCloseFormatted,
            expectedCloseDate: oppExpectedCloseDate,
            description: oppDescription,
            convertedAt: convertedAtFormatted,
            convertedAtRaw: lead.conversionDate || lead.createdAt,
            lastActivity: (() => {
              const t = lead.updatedAt || lead.createdAt;
              if (!t) return 'N/A';
              const d = new Date(t);
              return `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
            })(),
            leadEmail: lead.email || 'N/A',
            leadPhone: lead.phone || 'N/A',
            owner: lead.assignedTo
              ? `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim() || lead.assignedTo.email || 'Unknown'
              : 'Unknown',
            assignedTo: lead.assignedTo,
            leadId: lead
          };
        });

        setConvertedOpportunities(mappedOpportunities);
        setOpportunityPagination({
          currentPage: result.pagination?.currentPage || 1,
          totalPages: result.pagination?.totalPages || 1,
          totalItems: result.pagination?.totalLeads || mappedOpportunities.length,
          hasNextPage: result.pagination?.hasNextPage || false,
          hasPrevPage: result.pagination?.hasPrevPage || false,
          limit: result.pagination?.limit || opportunitiesItemsPerPage
        });
      }
    } catch (error) {
    } finally {
      setLoadingOpportunities(false);
    }
  }, [opportunitySearchTerm, statusFilter, dateRange, opportunitiesCurrentPage, opportunitiesItemsPerPage]);

  useEffect(() => {
    fetchConvertedOpportunities();
  }, [fetchConvertedOpportunities]);

  const fetchLeadStatuses = useCallback(async () => {
    try {
      const response = await leadStatusService.getDropdown();
      if (response && response.success) {
        setLeadStatuses(response.data || []);
      }
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    fetchLeadStatuses();
  }, [fetchLeadStatuses]);

  const handlePageChange = (newPage) => {
    setOpportunitiesCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setOpportunitiesItemsPerPage(newLimit);
    setOpportunitiesCurrentPage(1);
  };

  const handleClearFilters = () => {
    setOpportunitySearchTerm('');
    setStatusFilter('all');
    setDateRange({ startDate: '', endDate: '' });
    setOpportunitiesCurrentPage(1);
  };

  const getStageColor = (stage, stageValue) => {
    const valueToCheck = (stageValue || stage || '').toLowerCase();
    switch (valueToCheck) {
      case 'open':
      case 'converted': return 'bg-blue-100 text-blue-800';
      case 'won':
      case 'closed won':
      case 'closed_won': return 'bg-emerald-100 text-emerald-800';
      case 'lost':
      case 'closed lost':
      case 'closed_lost': return 'bg-red-100 text-red-800';
      case 'contacted': return 'bg-green-100 text-green-800';
      case 'qualified': return 'bg-purple-100 text-purple-800';
      default: {
        if (valueToCheck.includes('won')) return 'bg-emerald-100 text-emerald-800';
        if (valueToCheck.includes('lost')) return 'bg-red-100 text-red-800';
        if (valueToCheck.includes('contact')) return 'bg-green-100 text-green-800';
        if (valueToCheck.includes('qualif')) return 'bg-purple-100 text-purple-800';
        return 'bg-gray-100 text-gray-800';
      }
    }
  };

  // Modal Handler Functions
  const handleViewOpportunity = (opportunityId) => {
    const opportunity = convertedOpportunities.find(opp => opp.id === opportunityId);
    if (opportunity) {
      setSelectedOpportunity(opportunity);
      setShowViewModal(true);
    }
  };

  const handleEditOpportunity = (opportunityId) => {
    const opportunity = convertedOpportunities.find(opp => opp.id === opportunityId);
    if (opportunity) {
      setSelectedOpportunity(opportunity);
      setShowEditModal(true);
    }
  };

  const handleDeleteOpportunity = (opportunityId) => {
    const opportunity = convertedOpportunities.find(opp => opp.id === opportunityId);
    if (opportunity) {
      if (opportunity.stage === 'Closed Won' || opportunity.stageValue === 'closed_won') {
        showNotification('Closed Won opportunities cannot be deleted', 'error');
        return;
      }
      setOpportunityToDelete(opportunityId);
      setShowDeleteConfirmModal(true);
    }
  };

  const cancelDeleteOpportunity = () => {
    setShowDeleteConfirmModal(false);
    setOpportunityToDelete(null);
  };

  const confirmDeleteOpportunity = async () => {
    if (!opportunityToDelete) return;
    try {
      const result = await leadService.deleteLead(opportunityToDelete);
      if (result.success) {
        toast.success('Opportunity deleted successfully');
        fetchConvertedOpportunities();
        // Also refresh dashboard stats
        queryClient.invalidateQueries({ queryKey: ['pipelineDashboard'] });
      } else {
        toast.error(result.error || 'Failed to delete opportunity');
      }

    } catch (error) {
      toast.error(error.message || 'Error deleting opportunity');

    } finally {
      cancelDeleteOpportunity();
    }
  };

  const handleSaveOpportunity = async (updatedData) => {
    if (!selectedOpportunity) return;
    setIsSaving(true);
    try {
      const result = await leadService.updateLead(selectedOpportunity._id || selectedOpportunity.id, updatedData);
      if (result.success) {
        showNotification('Opportunity updated successfully', 'success');
        setShowEditModal(false);
        fetchConvertedOpportunities();
        // Also refresh dashboard stats
        queryClient.invalidateQueries({ queryKey: ['pipelineDashboard'] });
      } else {
        showNotification(result.error || 'Failed to update opportunity', 'error');
      }
    } catch (error) {
      showNotification('Error updating opportunity', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;

    // Safety check: Don't allow deleting Closed Won opportunities in bulk
    const selectedItems = convertedOpportunities.filter(opp => selectedRows.has(opp.id));
    const hasClosedWon = selectedItems.some(opp => opp.stage === 'Won' || opp.stageValue === 'closed_won');

    if (hasClosedWon) {
      toast.error('Cannot delete Won opportunities. Please deselect them first.');
      return;
    }

    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const idsToDelete = Array.from(selectedRows);
      const result = await leadService.bulkDeleteLeads(idsToDelete);

      if (result.success) {
        toast.success(result.message || 'Opportunities deleted successfully');
        setSelectedRows(new Set());
        fetchConvertedOpportunities();
        // Also refresh dashboard stats
        queryClient.invalidateQueries({ queryKey: ['pipelineDashboard'] });
      } else {
        toast.error(result.error || 'Failed to delete opportunities');
      }
    } catch (error) {
      toast.error('An error occurred while deleting opportunities');
    } finally {
      setIsBulkDeleting(false);
      setShowBulkDeleteConfirm(false);
    }
  };

  // Selection handlers
  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (items) => {
    if (items.length === 0) {
      setSelectedRows(new Set());
      return;
    }

    const allSelected = items.every(item => selectedRows.has(item.id));

    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (allSelected) {
        items.forEach(item => newSelection.delete(item.id));
      } else {
        items.forEach(item => newSelection.add(item.id));
      }
      return newSelection;
    });
  };

  const baseExportFilters = useMemo(
    () => ({
      search: opportunitySearchTerm.trim() || undefined,
      stage: statusFilter !== 'all' ? statusFilter : undefined,
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
    }),
    [
      opportunitySearchTerm,
      statusFilter,
      dateRange.startDate,
      dateRange.endDate,
    ]
  );

  const exportSortBy = useMemo(() => {
    const m = {
      opportunityName: 'opportunityName',
      value: 'opportunityValue',
      stage: 'stage',
      convertedAt: 'conversionDate',
      expectedClose: 'conversionDate',
      expectedCloseDate: 'conversionDate',
      leadName: 'lastName',
      company: 'company',
      owner: 'updatedAt',
    };
    return m[sortColumn] || 'conversionDate';
  }, [sortColumn]);

  const currentPageIdsForExport = useMemo(
    () => sortedOpportunities.map((o) => String(o.id)).filter(Boolean),
    [sortedOpportunities]
  );

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageIdsForExport)
          : [];
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'crm-opportunity-pipeline',
            format,
            scope,
            filters: {
              ...baseExportFilters,
              ...(overrideFilters != null ? overrideFilters : {}),
            },
            page: overridePage ?? opportunitiesCurrentPage,
            limit: overrideLimit ?? opportunitiesItemsPerPage,
            sortBy: exportSortBy,
            sortOrder: sortDirection,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `opportunity-pipeline-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      opportunitiesCurrentPage,
      opportunitiesItemsPerPage,
      exportSortBy,
      sortDirection,
      currentPageIdsForExport,
    ]
  );

  const handlePipelineSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected rows to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} opportunity(ies) exported.`);
        setShowInlineSelectedFormats(false);
        setSelectedRows(new Set());
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handlePipelineScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      const pageItems = sortedOpportunities;
      const totalFiltered = opportunityPagination?.totalItems ?? 0;

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !pageItems.length) {
          toast.error('No data on current page to export.');
          return;
        }

        if (scope === 'filtered' && totalFiltered === 0) {
          toast.error('No opportunities match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const rangeStart = meta?.startDate;
          const rangeEnd = meta?.endDate;
          if (!rangeStart || !rangeEnd) {
            toast.error('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate: rangeStart,
              endDate: rangeEnd,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          return;
        }

        toast.success('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      baseExportFilters,
      opportunityPagination?.totalItems,
      sortedOpportunities,
      requestBackendExport,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <XCircle className="h-6 w-6 text-red-500 mr-2" />
          <h3 className="text-lg font-medium text-red-800">Error Loading Data</h3>
        </div>
        <p className="mt-2 text-red-700">{error?.message || 'Failed to load dashboard data. Please try again.'}</p>
        <Button
          variant="outline"
          className="mt-4 bg-white border-red-300 text-red-700 hover:bg-red-50"
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </div>
    );
  }

  // Use real data from API
  const data = dashboardData;

  if (!data) return null;

  return (
    <>
      <div className="space-y-6">
        {/* Notification */}
        {notification && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${notification.type === 'success' ? 'bg-green-500 text-white' :
            notification.type === 'error' ? 'bg-red-500 text-white' :
              'bg-blue-500 text-white'
            }`}>
            {notification.message}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Opportunities</h1>
            <p className="text-gray-600">Comprehensive overview of your sales pipeline and converted opportunities</p>
          </div>
          <div className="flex items-center space-x-3">
            {selectedRows.size > 0 && user?.role === 'super_admin' && (
              <PermissionGate permission="opportunity_management" action="delete">
                <Button
                  variant="danger"
                  onClick={handleBulkDelete}
                  className="w-full sm:w-auto h-10"
                  icon={<Trash2 className="h-4 w-4" />}
                >
                  Delete ({selectedRows.size})
                </Button>
              </PermissionGate>
            )}
            <PermissionGate permission="opportunity_management" action="read">
              <div className="relative shrink-0" ref={selectedExportWrapRef}>
                <Button
                  variant="outline"
                  onClick={handleExportClick}
                  icon={<Download className="h-4 w-4" />}
                  disabled={exportLoading || loadingOpportunities}
                  className={`w-full sm:w-auto h-10${
                    selectedRows.size > 0
                      ? ' bg-blue-50 border-blue-200 text-blue-600'
                      : ''
                  }`}
                >
                  {selectedRows.size > 0
                    ? `Export Selected (${selectedRows.size})`
                    : 'Export'}
                </Button>
                <SelectedExportFormatBar
                  show={selectedRows.size > 0 && showInlineSelectedFormats}
                  panelEntered={inlineFormatPanelEntered}
                  disabled={exportLoading}
                  onSelect={handlePipelineSelectedFormatExport}
                />
              </div>
            </PermissionGate>
          </div>
        </div>

        {/* Main KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className='flex flex-col gap-2'>
                <p className="text-sm font-medium text-gray-600">Opportunities</p>
                <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(data.closures?.totalOpportunities || 0)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className='flex flex-col gap-2'>
                <p className="text-sm font-medium text-gray-600">Close Won</p>
                <p className="text-2xl font-bold text-green-600">{formatIndianNumber(data.closures.closedWon)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className='flex flex-col gap-2'>
                <p className="text-sm font-medium text-gray-600">Close Lost</p>
                <p className="text-2xl font-bold text-red-600">{formatIndianNumber(data.closures.closedLost)}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className='flex flex-col gap-2'>
                <p className="text-sm font-medium text-gray-600">Win Rate</p>
                <p className="text-2xl font-bold text-gray-900">{data.closures.winRate.toFixed(1)}%</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <BarChart3 className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Converted Opportunities List Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-end gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full">
              <div className="relative w-full flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={opportunitySearchTerm}
                  placeholder="Search opportunity name, Id,Lead,Company..."
                  onChange={(e) => {
                    const sanitized = handleSearchInputChange(e, setOpportunitySearchTerm, FIELD_LIMITS.search);
                    setOpportunitySearchTerm(sanitized);
                    setOpportunitiesCurrentPage(1);
                  }}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full h-10"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setOpportunitiesCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full sm:w-48 h-10"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="closed_won">Won</option>
                <option value="closed_lost">Lost</option>
              </select>

              <DateRangeFilter
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={setDateRange}
                allowFutureDates={true}
              />

              {(opportunitySearchTerm || statusFilter !== 'all' || dateRange.startDate || dateRange.endDate) && (
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  onClick={handleClearFilters}
                  className="w-full sm:w-auto h-10"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                      checked={convertedOpportunities.length > 0 && convertedOpportunities.every(item => selectedRows.has(item.id))}
                      onChange={() => handleSelectAll(convertedOpportunities)}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lead ID
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('opportunityName')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Opportunity</span>
                      {sortColumn === 'opportunityName' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('leadName')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Lead Details</span>
                      {sortColumn === 'leadName' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider select-none">
                    <div className="flex items-center space-x-1">
                      <span>Assignee</span>
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('value')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Value</span>
                      {sortColumn === 'value' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('stage')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'stage' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('convertedAt')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Converted At</span>
                      {sortColumn === 'convertedAt' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('expectedClose')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Expected Close</span>
                      {sortColumn === 'expectedClose' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingOpportunities ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mr-3"></div>
                        Loading opportunities...
                      </div>
                    </td>
                  </tr>
                ) : convertedOpportunities.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                      <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      No opportunities found.
                    </td>
                  </tr>
                ) : (
                  sortedOpportunities.map((opportunity) => (
                    <tr key={opportunity.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                          checked={selectedRows.has(opportunity.id)}
                          onChange={() => handleSelectRow(opportunity.id)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {opportunity.opportunityId ? (
                          <button
                            onClick={() => navigate(`/lead_detail_360/${opportunity.id}`)}
                            className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors"
                            title="View Lead Details"
                          >
                            {opportunity.opportunityId}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[200px] capitalize" title={opportunity.opportunityName}>
                          {truncateText(opportunity.opportunityName, 30)}
                        </div>
                        <div className="text-xs text-gray-500">ID: {opportunity.opportunityId || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[200px] capitalize" title={opportunity.leadName}>
                          {truncateText(opportunity.leadName, 25)}
                        </div>
                        <div className="text-xs text-gray-500 truncate max-w-[200px] capitalize" title={opportunity.company}>
                          {truncateText(opportunity.company, 25)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[200px] capitalize" title={opportunity.assignedTo ? `${opportunity.assignedTo.firstName || ''} ${opportunity.assignedTo.lastName || ''}`.trim() : 'Unassigned'}>
                          {opportunity.assignedTo ? `${opportunity.assignedTo.firstName || ''} ${opportunity.assignedTo.lastName || ''}`.trim() : 'Unassigned'}
                        </div>
                        {opportunity.assignedTo?.email && (
                          <div className="text-xs text-gray-500 truncate max-w-[200px]" title={opportunity.assignedTo.email}>
                            {opportunity.assignedTo.email}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold">
                        ₹{formatIndianAmount(opportunity.value)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStageColor(opportunity.stage, opportunity.stageValue)}`}>
                          {(() => {
                            const foundStatus = leadStatuses.find(s => s.value === opportunity.stageValue);
                            return foundStatus ? foundStatus.name : opportunity.stage;
                          })()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                        {opportunity.convertedAt}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {opportunity.expectedClose}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <PermissionGate permission="lead_management" action="read">
                            <button
                              onClick={() => handleViewOpportunity(opportunity.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Opportunity"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="lead_management" action="update">
                            <button
                              onClick={() => handleEditOpportunity(opportunity.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          {user?.role === 'super_admin' && (
                            <PermissionGate permission="lead_management" action="delete">
                              <span title={opportunity.stage === 'Closed Won' || opportunity.stageValue === 'closed_won' ? "Closed Won opportunities cannot be deleted" : ""}>
                                <button
                                  onClick={() => !(opportunity.stage === 'Closed Won' || opportunity.stageValue === 'closed_won') && handleDeleteOpportunity(opportunity.id)}
                                  className={`${opportunity.stage === 'Closed Won' || opportunity.stageValue === 'closed_won' ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-900'}`}
                                  disabled={opportunity.stage === 'Closed Won' || opportunity.stageValue === 'closed_won'}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </span>
                            </PermissionGate>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Section */}
          {(() => {
            const opportunitiesTotalItems = Number(opportunityPagination?.totalItems || convertedOpportunities.length || 0);
            const opportunitiesTotalPages = Number(opportunityPagination?.totalPages || 1);
            const effectivePaginationLimit = opportunityPagination?.limit || opportunitiesItemsPerPage || 10;
            // Show pagination if there's more than one page
            const shouldShowPagination = !loadingOpportunities && (opportunitiesTotalPages > 1 || convertedOpportunities.length > 0);

            return shouldShowPagination ? (
              <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                  {/* Results Count and Limit Selector */}
                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-700">
                      Showing {((opportunityPagination.currentPage || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((opportunityPagination.currentPage || 1) * effectivePaginationLimit, opportunitiesTotalItems)} of {opportunitiesTotalItems} results
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-700">Items per page:</label>
                      <select
                        value={opportunitiesItemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value={3}>3</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange((opportunityPagination.currentPage || 1) - 1)}
                      disabled={opportunityPagination.hasPrevPage === false || (opportunityPagination.currentPage || 1) <= 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {(() => {
                        const totalPages = opportunityPagination.totalPages || 1;
                        const currentPageNum = opportunityPagination.currentPage || 1;
                        const pages = [];

                        if (totalPages <= 10) {
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push(1);

                          if (currentPageNum <= 4) {
                            for (let i = 2; i <= 5; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPages);
                          } else if (currentPageNum >= totalPages - 3) {
                            pages.push('ellipsis');
                            for (let i = totalPages - 4; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            pages.push('ellipsis');
                            for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPages);
                          }
                        }

                        return pages.map((page, index) => {
                          if (page === 'ellipsis') {
                            return (
                              <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                ...
                              </span>
                            );
                          }

                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                ? 'bg-agrosaf-green text-white'
                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                              {page}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    <button
                      onClick={() => handlePageChange((opportunityPagination.currentPage || 1) + 1)}
                      disabled={opportunityPagination.hasNextPage === false || (opportunityPagination.currentPage || 1) >= (opportunityPagination.totalPages || 1)}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            ) : null;
          })()}
        </div>
      </div >

      {/* View Opportunity Modal */}
      {
        showViewModal && selectedOpportunity && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Opportunity Details</h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Opportunity Name</p>
                        <p className="text-sm font-medium text-gray-900 break-words capitalize" title={selectedOpportunity.opportunityName}>{selectedOpportunity.opportunityName || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Lead Name</p>
                        <p className="text-sm font-medium text-gray-900 break-words capitalize" title={selectedOpportunity.leadName}>{selectedOpportunity.leadName || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Company</p>
                        <p className="text-sm font-medium text-gray-900 break-words first-letter:capitalize" title={selectedOpportunity.company}>{selectedOpportunity.company || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Opportunity Value</p>
                        <p className="text-sm font-medium text-gray-900 break-words" title={`₹${selectedOpportunity.value ? formatIndianAmount(selectedOpportunity.value) : '0'}`}>
                          ₹{selectedOpportunity.value ? formatIndianAmount(selectedOpportunity.value) : '0'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Expected Close</p>
                        <p className="text-sm font-medium text-gray-900 break-words" title={selectedOpportunity.expectedClose}>{selectedOpportunity.expectedClose || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Contact Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="text-sm font-medium text-gray-900 break-words" title={selectedOpportunity.leadEmail || 'N/A'}>{selectedOpportunity.leadEmail || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="text-sm font-medium text-gray-900 break-words" title={selectedOpportunity.leadPhone || 'N/A'}>{selectedOpportunity.leadPhone || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Additional Details */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div>
                      <p className="text-sm text-gray-600">Description</p>
                      <p className="text-sm font-medium text-gray-900 break-words first-letter:capitalize" title={selectedOpportunity.description || 'No description'}>{selectedOpportunity.description || 'No description'}</p>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Timeline</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Converted At</p>
                        <p className="text-sm font-medium text-gray-900">{selectedOpportunity.convertedAt || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Last Activity</p>
                        <p className="text-sm font-medium text-gray-900">{selectedOpportunity.lastActivity || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={() => setShowViewModal(false)}
                  >
                    Close
                  </Button>
                  <PermissionGate permission="lead_management" action="update">
                    <Button
                      variant="primary"
                      onClick={() => {
                        setShowViewModal(false);
                        handleEditOpportunity(selectedOpportunity.id);
                      }}
                      className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                    >
                      Edit Opportunity
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Edit Opportunity Modal */}
      {
        showEditModal && selectedOpportunity && (
          <OpportunityEditModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            opportunity={selectedOpportunity}
            onSave={handleSaveOpportunity}
            isSaving={isSaving}
          />
        )
      }

      {/* Delete Opportunity Confirmation Modal */}
      {
        showDeleteConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                  <button
                    onClick={cancelDeleteOpportunity}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <p className="text-sm text-gray-700">
                    Are you sure you want to delete this opportunity? This action cannot be undone.
                  </p>
                </div>

                {/* Modal Actions */}
                <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={cancelDeleteOpportunity}
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="lead_management" action="delete">
                    <Button
                      variant="primary"
                      onClick={confirmDeleteOpportunity}
                      className="bg-red-500 hover:bg-red-700"
                    >
                      Delete Opportunity
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 z-[100] bg-black bg-opacity-50 flex items-center justify-center p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Confirm Bulk Delete</h3>
              </div>

              <p className="text-gray-600 mb-6">
                Are you sure you want to delete <span className="font-bold text-red-600">{selectedRows.size}</span> selected opportunities? This action cannot be undone.
              </p>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  disabled={isBulkDeleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={confirmBulkDelete}
                  loading={isBulkDeleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete Selected
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Opportunities"
        showDateRange
        scopedFormatExport={{
          onExport: handlePipelineScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
    </>
  );
};

export default PipelineDashboard;